import { useMemo } from 'react';
import { useQuery } from '@tanstack/react-query';
import type { PurchaseReturnRow } from '@order-pilot/shared';
import { AsyncSection } from '@/components/AsyncSection';
import { Badge } from '@/components/Badge';
import { PageHeader } from '@/components/PageHeader';
import {
  ReportColumnsButton,
  ReportFiltersButton,
  ReportSearchField,
  ReportToolbar,
} from '@/components/ReportToolbar';
import { SectionCard } from '@/components/SectionCard';
import { ErrorState } from '@/components/States';
import { applyReportLayout, DataTable, TableSkeleton } from '@/components/Table';
import type { Column } from '@/components/Table';
import { KpiGrid, KpiTile, KpiTileSkeleton } from '@/features/portal/KpiTile';
import { ExportMenu } from '@/features/portal/ExportMenu';
import { usePortal } from '@/features/portal/PortalContext';
import { fetchPurchaseReturns, portalKeys } from '@/features/portal/api';
import { toReportRange } from '@/features/portal/filters';
import { applyClientFilters } from '@/features/reports/applyClientFilters';
import { useReportLayout } from '@/features/reports/useReportLayout';
import { useReportToolbar } from '@/features/reports/useReportToolbar';
import { formatCount, formatMoney, formatPosDate } from '@/lib/format';

/** 'Returned' is the settled state on a purchase return, the POS's default. */
function statusTone(status: string): 'success' | 'warning' | 'danger' | 'neutral' {
  switch (status.trim().toLowerCase()) {
    case 'returned':
    case 'refunded':
      return 'success';
    case 'pending':
      return 'warning';
    case 'rejected':
      return 'danger';
    default:
      return 'neutral';
  }
}

const FILTER_FIELDS = ['Purchase', 'Supplier', 'Reason', 'Status'] as const;

const TOGGLEABLE = ['branch', 'purchaseNumber', 'reason', 'status', 'refund'] as const;

const COLUMN_LABELS: Record<string, string> = {
  branch: 'Branch',
  purchaseNumber: 'Purchase',
  reason: 'Reason',
  status: 'Status',
  refund: 'Refund',
};

export function PurchaseReturnsPage() {
  const { currency, filters, multiBranch } = usePortal();
  const range = useMemo(() => toReportRange(filters.filters), [filters.filters]);
  const toolbar = useReportToolbar();
  const layout = useReportLayout('purchase-returns', TOGGLEABLE);

  const returnsQuery = useQuery({
    queryKey: portalKeys.purchaseReturns(range),
    queryFn: ({ signal }) => fetchPurchaseReturns(range, signal),
  });

  const report = returnsQuery.data;
  const reportCurrency = report?.currency || currency;

  const rows = useMemo(
    () =>
      applyClientFilters(report?.rows ?? [], {
        search: toolbar.search,
        filterRows: toolbar.filterRows,
        matchAny: toolbar.matchAny,
        fieldValue: (row, field) =>
          field === 'Purchase'
            ? row.purchaseNumber
            : field === 'Supplier'
              ? row.supplierName
              : field === 'Reason'
                ? row.reason
                : field === 'Status'
                  ? row.status
                  : undefined,
        searchValues: (row) => [row.returnNumber, row.purchaseNumber, row.supplierName, row.reason],
      }),
    [report, toolbar.search, toolbar.filterRows, toolbar.matchAny],
  );

  const baseColumns = useMemo<Array<Column<PurchaseReturnRow>>>(
    () => [
      {
        key: 'returnNumber',
        header: 'Return',
        pinned: true,
        cell: (row) => (
          <div>
            <p className="font-medium text-slate-900">{row.returnNumber}</p>
            <p className="mt-0.5 whitespace-nowrap text-xs text-slate-400">
              {formatPosDate(row.date)}
            </p>
          </div>
        ),
      },
      {
        key: 'branch',
        header: 'Branch',
        cell: (row) => <span className="text-slate-500">{row.branchName || '—'}</span>,
      },
      {
        key: 'purchaseNumber',
        header: 'Purchase',
        cell: (row) => <span className="text-slate-500">{row.purchaseNumber || '—'}</span>,
      },
      {
        key: 'supplier',
        header: 'Supplier',
        pinned: true,
        cell: (row) => <span className="text-slate-700">{row.supplierName || '—'}</span>,
      },
      {
        key: 'reason',
        header: 'Reason',
        cell: (row) => <span className="text-slate-500">{row.reason || '—'}</span>,
      },
      {
        key: 'status',
        header: 'Status',
        cell: (row) => <Badge tone={statusTone(row.status)}>{row.status || '—'}</Badge>,
      },
      {
        key: 'refund',
        header: 'Refund',
        align: 'right',
        cellClassName: 'tabular-nums font-medium',
        cell: (row) => (
          <span className="text-emerald-700">
            {formatMoney(row.refundAmount, reportCurrency)}
          </span>
        ),
      },
    ],
    [reportCurrency],
  );

  const columns = useMemo(() => {
    const resolved = applyReportLayout(baseColumns, layout);
    return multiBranch ? resolved : resolved.filter((column) => column.key !== 'branch');
  }, [baseColumns, layout, multiBranch]);

  const header = (
    <PageHeader
      title="Purchase returns"
      description="Stock sent back to suppliers in the selected range."
      actions={
        <ExportMenu
          report="purchase-returns"
          range={range}
          disabled={returnsQuery.isPending || returnsQuery.isError}
        />
      }
    />
  );

  if (returnsQuery.isError) {
    return (
      <>
        {header}
        <div className="card">
          <ErrorState
            error={returnsQuery.error}
            onRetry={() => void returnsQuery.refetch()}
            retrying={returnsQuery.isFetching}
          />
        </div>
      </>
    );
  }

  return (
    <>
      {header}

      <div className="space-y-6">
        <AsyncSection
          state={returnsQuery}
          skeleton={
            <KpiGrid>
              {Array.from({ length: 2 }).map((_, index) => (
                <KpiTileSkeleton key={index} />
              ))}
            </KpiGrid>
          }
        >
          {(data) => (
            <KpiGrid>
              <KpiTile
                label="Returned to suppliers"
                value={formatMoney(data.totalRefunded, data.currency || currency)}
                // Money coming BACK to the shop, so not a negative tone here —
                // the opposite of a sales refund.
                hint="Settled returns only"
              />
              <KpiTile label="Returns" value={formatCount(data.count)} />
            </KpiGrid>
          )}
        </AsyncSection>

        <SectionCard
          headingId="purchase-returns-table"
          title="Purchase return history"
          count={rows.length}
          description="Newest first."
        >
          <ReportToolbar>
            <ReportSearchField
              value={toolbar.search}
              onChange={toolbar.setSearch}
              placeholder="Return, purchase or supplier"
            />
            <ReportFiltersButton
              fields={FILTER_FIELDS}
              applied={toolbar.filterRows}
              matchAny={toolbar.matchAny}
              onApply={toolbar.applyFilters}
              placeholderFor={(field) => (field === 'Status' ? 'Returned / Pending / Rejected' : 'value')}
            />
            <ReportColumnsButton layout={layout} labels={COLUMN_LABELS} />
          </ReportToolbar>
          <AsyncSection
            state={returnsQuery}
            skeleton={<TableSkeleton rows={6} columns={6} />}
            isEmpty={() => rows.length === 0}
            emptyTitle={toolbar.search ? `No returns match “${toolbar.search}”` : 'No purchase returns in this range'}
            emptyDescription="Pick a wider date range, or check another branch or till."
          >
            {() => (
              <DataTable
                caption="Purchase returns with supplier, reason, status and refund"
                columns={columns}
                rows={rows}
                rowKey={(row) => row.id}
                layout={layout}
              />
            )}
          </AsyncSection>
        </SectionCard>
      </div>
    </>
  );
}
